import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { InputNumber } from 'antd';
import { debounce } from 'lodash-es';
import { TextField } from '@ui/field-text';
import { Joystick } from '@ui/joystick';
import { useSelectTypeTotalInfo } from '@widgets/create-warehouse/select-type/hooks/use-total-info.hook';
import { Scheme } from '@@/schemes/ts';
import { SchemeSelect } from '@@/schemes/components/scheme-select';
import {
  CreateWarehouseStep,
  ICreateWarehouseStepProps,
} from '@@/warehouses/components/create-warehouse-step';
import { UiTextField } from '@@/ui/components/ui-text-field';
import { UiButton } from '@@/ui/components/ui-button';

import { Radio } from '@consta/uikit/Radio';
import { IWarehouseAdjustment } from '@@/warehouses/ts';

import styles from './styles.module.css';
import classNames from 'classnames';
import { InputWithSignWheelControl } from '@/modules/common/components/input-with-sign-wheel';

export type BagSize = 60 | 75 | 'custom';
const BAG_SIZE_EXCLUDE_CUSTOM: number[] = [60, 75];

enum AngleDirection {
  right = 'right',
  left = 'left',
}

// TODO: декомпозировать
export function SelectType(props: ISelectTypeProps) {
  const {
    className,
    mapSize,
    sleeveSize,
    adjustment,
    bugsCount,
    bugsVolume,
    scheme,
    onUpdateSleeveSize,
    onUpdateAdjustment,
    onUpdateScheme,
    onCalculate,
    ...rest
  } = props;
  const rootClass = cn(styles.root, className);
  const [isCalculating, setIsCalculating] = useState(false);

  const [bagSize, setBagSize] = useState<BagSize | null>(null);
  useEffect(() => {
    if (!sleeveSize) return;

    if (BAG_SIZE_EXCLUDE_CUSTOM.includes(sleeveSize)) setBagSize(sleeveSize as BagSize);
    else setBagSize('custom');
  }, [sleeveSize]);

  const [customAngle, setCustomAngle] = useState<boolean>(false);

  if (customAngle) {
    adjustment.custom = customAngle;
  } else {
    delete adjustment.custom;
  }

  const absoluteAngle = Math.abs(adjustment.angle);
  const angleDirection =
    adjustment.angle >= 0 ? AngleDirection.right : AngleDirection.left;

  const onCalculateLocal: ISelectTypeProps['onCalculate'] = async (...args) => {
    if (!args[0].sleeveSize) return;

    setIsCalculating(true);
    await onCalculate(...args);
    setIsCalculating(false);
  };
  const onDebouncedCalculate = useMemo(() => debounce(onCalculateLocal, 500), []); // onCalculate не в качестве зависимости, т.к. он меняется при каждом рендере

  const onUpdateAngleDirection = (direction: AngleDirection) => {
    if (direction === angleDirection) return;
    const newAdjustment = {
      angle: direction === AngleDirection.right ? absoluteAngle : -absoluteAngle,
      x: adjustment.x,
      y: adjustment.y,
    };

    onUpdateAdjustment(newAdjustment);
    onCalculateLocal({ sleeveSize, adjustment: newAdjustment, scheme });
  };
  const onUpdateAngle = (absoluteAngle: number) => {
    setCustomAngle(true);
    const newAdjustment = {
      angle: angleDirection === AngleDirection.right ? absoluteAngle : -absoluteAngle,
      x: adjustment.x,
      y: adjustment.y,
    };

    onUpdateAdjustment(newAdjustment);
    onDebouncedCalculate.cancel();
    onDebouncedCalculate({ sleeveSize, adjustment: newAdjustment, scheme });
  };

  const ADJUSTMENT_JOYSTICK_STEP = 1; // TODO: вынести в константы
  const onUpdateJoystick = ({
    type,
    action,
  }: {
    type: 'x' | 'y';
    action: '+' | '-';
  }) => {
    const newAdjustment = {
      angle: adjustment.angle,
      x:
        type === 'x'
          ? adjustment.x +
            (action === '+' ? ADJUSTMENT_JOYSTICK_STEP : -ADJUSTMENT_JOYSTICK_STEP)
          : adjustment.x,
      y:
        type === 'y'
          ? adjustment.y +
            (action === '+' ? ADJUSTMENT_JOYSTICK_STEP : -ADJUSTMENT_JOYSTICK_STEP)
          : adjustment.y,
      // custom: false,
    };

    onUpdateAdjustment(newAdjustment);
    onDebouncedCalculate.cancel();
    onDebouncedCalculate({ sleeveSize, adjustment: newAdjustment, scheme });
  };

  const lines = useSelectTypeTotalInfo({
    mapSize,
    bugsCount,
    bugsVolume,
  });

  return (
    <CreateWarehouseStep
      className={rootClass}
      next={{ label: 'Сохранить склад', disabled: isCalculating || !bagSize }}
      {...rest}
    >
      <p className={styles.header}>выберите тип рукавов:</p>

      <ul className={styles.radios}>
        {BAG_SIZE_EXCLUDE_CUSTOM.map((size) => (
          <li key={size}>
            <Radio
              label={`${size} м`}
              checked={sleeveSize === size}
              disabled={isCalculating}
              onChange={() => {
                onUpdateSleeveSize(size);
                onCalculateLocal({ sleeveSize: size, adjustment, scheme });
              }}
            />
          </li>
        ))}
        <li>
          <Radio
            label="свой вариант"
            checked={bagSize === 'custom'}
            disabled={isCalculating}
            onChange={() => {
              // onUpdateSleeveSize(0);
              setBagSize('custom');
            }}
          />
        </li>
      </ul>

      {bagSize === 'custom' && (
        <div className={styles.custom}>
          <UiTextField
            className={styles.input}
            type="number"
            value={sleeveSize?.toString()}
            onKeyDown={(event) => {
              if (event.code === 'Enter') {
                event.preventDefault();
                onCalculateLocal({ sleeveSize, adjustment, scheme });
              }
            }}
            onClick={(elem: any) => (elem.target.value = '')}
            onChange={(event) => onUpdateSleeveSize(Number(event.value))}
          />

          <UiButton
            label="OK"
            view="primary"
            disabled={!sleeveSize}
            onClick={() => onCalculateLocal({ sleeveSize, adjustment, scheme })}
          />
        </div>
      )}

      {/* Adjustment */}
      <div className={styles.adjustment}>
        <div className={styles.angle}>
          <p>градус наклона рукавов</p>

          <div className={styles.angleContent}>
            {/* <TextField
              className={styles.angle__input}
              value={absoluteAngle.toString()}
              type="number"
              min={0}
              incrementButtons={true}
              // @ts-ignore
              onChange={({ value }) => onUpdateAngle(+value)}
              onClick={(elem) => (elem.target.value = '')}
            /> */}
            {/* <InputNumber
              id="type__input"
              value={absoluteAngle === 0 ? '' : absoluteAngle.toString()}
              type="number"
              min={"0"}
              onChange={
                // @ts-ignore
                (value) => onUpdateAngle(+value)
              }
            /> */}
            <InputWithSignWheelControl
              value={absoluteAngle === 0 ? '' : absoluteAngle.toString()}
              onChange={(e) => onUpdateAngle(+e.target.value.replace(/\D/g, ""))
                // @ts-ignore
                //(value) => onUpdateAngle(+value)
              }
              setValue={(value) => onUpdateAngle(+value)}
              postfix={'°'}
              className={styles.angleControl}
              inputClassName={styles.angleControlBody}
              //style={{ width: '60px', padding: '0px' }}
            />

            <div className={styles.handleSide}>
              <UiButton
                label="влево"
                form="brick"
                view={angleDirection === AngleDirection.left ? 'primary' : 'clear'}
                onClick={() => onUpdateAngleDirection(AngleDirection.left)}
                className={classNames(
                  styles.handleSideButton,
                  angleDirection === AngleDirection.left &&
                    styles.handleSideButtonActive,
                )}
              />
              <UiButton
                label="вправо"
                form="brick"
                view={angleDirection === AngleDirection.right ? 'primary' : 'clear'}
                onClick={() => onUpdateAngleDirection(AngleDirection.right)}
                className={classNames(
                  styles.handleSideButton,
                  angleDirection === AngleDirection.right &&
                    styles.handleSideButtonActive,
                )}
              />
            </div>
          </div>
        </div>

        <Joystick
          size="small"
          onTop={() => onUpdateJoystick({ type: 'y', action: '-' })}
          onBottom={() => onUpdateJoystick({ type: 'y', action: '+' })}
          onLeft={() => onUpdateJoystick({ type: 'x', action: '-' })}
          onRight={() => onUpdateJoystick({ type: 'x', action: '+' })}
        />
      </div>

      <SchemeSelect
        value={scheme}
        onChange={(value) => {
          onUpdateScheme(value);
          onCalculateLocal({ sleeveSize, adjustment, scheme: value });
        }}
        className={styles.schemeSelect}
      />

      <div className={styles.lines}>
        {lines.map(({ label, value }) => (
          <p className={styles.line} key={label}>
            <span className={styles.label}>{label}:</span>
            <span className={styles.value}>{value}</span>
          </p>
        ))}
      </div>
    </CreateWarehouseStep>
  );
}

export interface ISelectTypeProps extends ICreateWarehouseStepProps {
  bugsVolume?: number;
  bugsCount?: number;
  mapSize?: number;
  sleeveSize: number;
  adjustment: IWarehouseAdjustment;
  scheme: Scheme;
  onCalculate: (payload: {
    sleeveSize: number;
    adjustment: IWarehouseAdjustment;
    scheme: Scheme;
  }) => void | Promise<void>;
  onUpdateAdjustment: (adjustment: IWarehouseAdjustment) => void | Promise<void>;
  onUpdateSleeveSize: (size: number) => void | Promise<void>;
  onUpdateScheme: (scheme: Scheme) => void | Promise<void>;
}
